
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MessageShareProps {
  referralUrl: string | null;
}

export function MessageShare({ referralUrl }: MessageShareProps) {
  const { toast } = useToast();
  const [isCopying, setIsCopying] = useState(false);
  
  const getCustomMessage = () => {
    if (!referralUrl) return '';
    return `I've been training BJJ with this amazing app! Check out The Mat Monster and get 30% off with my link: ${referralUrl}`;
  };
  
  const customMessage = getCustomMessage();
  
  const handleCopyMessage = async () => {
    if (!customMessage) {
      toast({
        title: "No message available",
        description: "Please wait while we generate your referral message.",
        variant: "destructive"
      });
      return;
    }
    
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(customMessage);
      toast({
        title: "Message copied to clipboard",
        description: "Your referral message has been copied to your clipboard."
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCopying(false);
    }
  };
  
  // Handle sharing to specific platforms
  const handleShareEmail = () => {
    const subject = encodeURIComponent("Check out The Mat Monster BJJ App");
    const body = encodeURIComponent(customMessage);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };
  
  return (
    <div className="space-y-4">
      <p className="text-sm">Share this pre-written message:</p>
      <div className="rounded-md border p-4 bg-muted/30">
        <p className="text-sm">{customMessage || 'Loading message...'}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={handleCopyMessage}
          disabled={isCopying || !customMessage}
          variant="outline"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          {isCopying ? 'Copying...' : 'Copy Message'}
        </Button>
        
        <Button
          onClick={handleShareEmail}
          disabled={!customMessage}
          variant="outline"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Email
        </Button>
      </div>
    </div>
  );
}
